import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { ComposableMap, Geographies, Geography, ZoomableGroup, Marker } from 'react-simple-maps';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import api from '../lib/api';

const GEO_URL = '/india-states.json';

const STATE_NAME_MAP = {
  'Andhra Pradesh': 'Andhra Pradesh', 'Arunachal Pradesh': 'Arunachal Pradesh',
  'Assam': 'Assam', 'Bihar': 'Bihar', 'Chhattisgarh': 'Chhattisgarh',
  'Goa': 'Goa', 'Gujarat': 'Gujarat', 'Haryana': 'Haryana',
  'Himachal Pradesh': 'Himachal Pradesh', 'Jharkhand': 'Jharkhand',
  'Karnataka': 'Karnataka', 'Kerala': 'Kerala', 'Madhya Pradesh': 'Madhya Pradesh',
  'Maharashtra': 'Maharashtra', 'Manipur': 'Manipur', 'Meghalaya': 'Meghalaya',
  'Mizoram': 'Mizoram', 'Nagaland': 'Nagaland', 'Odisha': 'Odisha',
  'Punjab': 'Punjab', 'Rajasthan': 'Rajasthan', 'Sikkim': 'Sikkim',
  'Tamil Nadu': 'Tamil Nadu', 'Telangana': 'Telangana', 'Tripura': 'Tripura',
  'Uttar Pradesh': 'Uttar Pradesh', 'Uttarakhand': 'Uttarakhand',
  'West Bengal': 'West Bengal', 'Delhi': 'Delhi', 'Jammu and Kashmir': 'Jammu and Kashmir',
  'Ladakh': 'Ladakh', 'Chandigarh': 'Chandigarh', 'Puducherry': 'Puducherry',
  'Andaman and Nicobar': 'Andaman and Nicobar', 'Dadra and Nagar Haveli': 'Dadra and Nagar Haveli',
  'Daman and Diu': 'Daman and Diu', 'Lakshadweep': 'Lakshadweep',
  'NCT of Delhi': 'Delhi',
};

const CITY_COORDS = {
  'Mumbai': [19.076, 72.8777], 'Delhi': [28.7041, 77.1025], 'Bangalore': [12.9716, 77.5946],
  'Bengaluru': [12.9716, 77.5946], 'Hyderabad': [17.385, 78.4867], 'Ahmedabad': [23.0225, 72.5714],
  'Chennai': [13.0827, 80.2707], 'Kolkata': [22.5726, 88.3639], 'Pune': [18.5204, 73.8567],
  'Jaipur': [26.9124, 75.7873], 'Surat': [21.1702, 72.8311], 'Lucknow': [26.8467, 80.9462],
  'Kanpur': [26.4499, 80.3319], 'Nagpur': [21.1458, 79.0882], 'Indore': [22.7196, 75.8577],
  'Thane': [19.2183, 72.9781], 'Bhopal': [23.2599, 77.4126], 'Visakhapatnam': [17.6868, 83.2185],
  'Patna': [25.6093, 85.1376], 'Vadodara': [22.3072, 73.1812], 'Rajkot': [22.3039, 70.8022],
  'Bhavnagar': [21.7645, 72.1519], 'Vapi': [20.3893, 72.9106], 'Ludhiana': [30.901, 75.8573],
  'Agra': [27.1767, 78.0081], 'Varanasi': [25.3176, 82.9739], 'Madurai': [9.9252, 78.1198],
  'Coimbatore': [11.0168, 76.9558], 'Kochi': [9.9312, 76.2673], 'Guwahati': [26.1445, 91.7362],
  'Chandigarh': [30.7333, 76.7794], 'Ranchi': [23.3441, 85.3096], 'Raipur': [21.2514, 81.6296],
  'Jodhpur': [26.2389, 73.0243], 'Udaipur': [24.5854, 73.7125], 'Goa': [15.2993, 74.124],
  'Bhubaneswar': [20.2961, 85.8245], 'Dehradun': [30.3165, 78.0322], 'Amritsar': [31.634, 74.8723],
  'Thiruvananthapuram': [8.5241, 76.9366], 'Noida': [28.5355, 77.391], 'Gurugram': [28.4595, 77.0266],
  'Gurgaon': [28.4595, 77.0266], 'Faridabad': [28.4089, 77.3178], 'Ghaziabad': [28.6692, 77.4538],
  'North Delhi': [28.7562, 77.1599], 'South Delhi': [28.5244, 77.2066],
  'New Delhi': [28.6139, 77.209], 'Jammu': [32.7266, 74.857], 'Srinagar': [34.0837, 74.7973],
};

const fmt = (v) => {
  if (v === null || v === undefined) return '0';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 10000000) return `${sign}${(abs / 10000000).toFixed(1)}Cr`;
  if (abs >= 100000) return `${sign}${(abs / 100000).toFixed(1)}L`;
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)}K`;
  return v?.toFixed(0) || '0';
};

const getColor = (turnover, maxTurnover, growth) => {
  if (!turnover || turnover === 0) return '#e5e7eb';
  if (growth > 20) return '#059669';
  if (growth > 0) return '#34d399';
  if (growth === 0) return '#fbbf24';
  if (growth > -20) return '#f97316';
  return '#ef4444';
};

const GrowthBadge = ({ growth }) => {
  if (growth === undefined || growth === null) return null;
  const isPositive = growth > 0;
  const isNeutral = growth === 0;
  return (
    <span className={`inline-flex items-center text-xs font-bold px-1.5 py-0.5 rounded ${isNeutral ? 'bg-yellow-100 text-yellow-700' : isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
      {isPositive ? '+' : ''}{growth}%
    </span>
  );
};

const FY_QUARTERS = [
  { value: '1', label: 'Q1 (Apr-Jun)' },
  { value: '2', label: 'Q2 (Jul-Sep)' },
  { value: '3', label: 'Q3 (Oct-Dec)' },
  { value: '4', label: 'Q4 (Jan-Mar)' },
];

const COMPARE_COLORS = ['#4f46e5', '#059669', '#d97706', '#dc2626'];

// Format date to dd-mm-yy
const fmtDateDMY = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d + 'T00:00:00');
  if (isNaN(dt.getTime())) return String(d);
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const yy = String(dt.getFullYear()).slice(-2);
  return `${dd}-${mm}-${yy}`;
};

// Generate FY options going back several years from current
const generateFYOptions = (availableFYs) => {
  if (availableFYs && availableFYs.length > 0) {
    // Extend range: include 4 years before earliest and 1 after latest
    const years = availableFYs.map(f => f.start_year);
    const min = Math.min(...years) - 4;
    const max = Math.max(...years) + 1;
    const opts = [];
    for (let y = max; y >= min; y--) {
      opts.push({ start_year: y, label: `FY ${y}-${String(y + 1).slice(-2)}` });
    }
    return opts;
  }
  const now = new Date();
  const curFY = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const opts = [];
  for (let i = 0; i < 6; i++) {
    const y = curFY - i;
    opts.push({ start_year: y, label: `FY ${y}-${String(y + 1).slice(-2)}` });
  }
  return opts;
};

const currentFYStart = () => {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
};

const currentFYQuarter = () => {
  const m = new Date().getMonth() + 1;
  if (m >= 4 && m <= 6) return '1';
  if (m >= 7 && m <= 9) return '2';
  if (m >= 10 && m <= 12) return '3';
  return '4';
};


// =====================
// COMPARISON VIEW
// =====================

const MONTHS_LIST = [
  { value: 1, label: 'Jan' }, { value: 2, label: 'Feb' }, { value: 3, label: 'Mar' },
  { value: 4, label: 'Apr' }, { value: 5, label: 'May' }, { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' }, { value: 8, label: 'Aug' }, { value: 9, label: 'Sep' },
  { value: 10, label: 'Oct' }, { value: 11, label: 'Nov' }, { value: 12, label: 'Dec' },
];

const COMPARE_MODES = [
  { key: 'fy', label: 'Yearly' },
  { key: 'quarterly', label: 'Quarterly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'custom', label: 'Custom Range' },
];

const generateYearList = () => {
  const now = new Date();
  const opts = [];
  for (let y = now.getFullYear() + 1; y >= now.getFullYear() - 6; y--) {
    opts.push(y);
  }
  return opts;
};

const ComparisonView = ({ user }) => {
  const [compareMode, setCompareMode] = useState('fy');

  // FY selections
  const [fySelections, setFySelections] = useState(() => {
    const cur = currentFYStart();
    return [cur, cur - 1, cur - 2, cur - 3];
  });

  // Quarterly selections: [{fy: 2025, q: 1}, ...]
  const [qtrSelections, setQtrSelections] = useState(() => {
    const curFY = currentFYStart();
    const curQ = parseInt(currentFYQuarter());
    const items = [];
    let fy = curFY, q = curQ;
    for (let i = 0; i < 4; i++) {
      items.push({ fy, q });
      q--;
      if (q < 1) { q = 4; fy--; }
    }
    return items;
  });

  // Monthly selections: [{year: 2026, month: 3}, ...]
  const [monthSelections, setMonthSelections] = useState(() => {
    const now = new Date();
    const items = [];
    let m = now.getMonth() + 1, y = now.getFullYear();
    for (let i = 0; i < 4; i++) {
      items.push({ year: y, month: m });
      m--;
      if (m < 1) { m = 12; y--; }
    }
    return items;
  });

  // Custom ranges
  const [customRanges, setCustomRanges] = useState([
    { start: null, end: null },
    { start: null, end: null },
  ]);

  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedState, setExpandedState] = useState(null);
  const [availableFYs, setAvailableFYs] = useState([]);

  const fyOptions = useMemo(() => generateFYOptions(availableFYs), [availableFYs]);
  const yearList = useMemo(() => generateYearList(), []);

  const loadComparison = useCallback(async () => {
    setLoading(true);
    try {
      let params = `mode=${compareMode}`;
      if (compareMode === 'fy') {
        params += `&fy_years=${fySelections.join(',')}`;
      } else if (compareMode === 'quarterly') {
        const qParts = qtrSelections.map(s => `${s.fy}-${s.q}`).join(',');
        params += `&quarters=${qParts}`;
      } else if (compareMode === 'monthly') {
        const mParts = monthSelections.map(s => `${s.year}-${String(s.month).padStart(2, '0')}`).join(',');
        params += `&months=${mParts}`;
      } else {
        const parts = customRanges
          .filter(r => r.start && r.end)
          .flatMap(r => [r.start.toISOString().slice(0, 10), r.end.toISOString().slice(0, 10)]);
        if (parts.length >= 2) params += `&ranges=${parts.join(',')}`;
      }
      const res = await api.get(`/geo-dashboard/compare?${params}`);
      setCompareData(res.data);
      if (res.data.available_fys) setAvailableFYs(res.data.available_fys);
    } catch {
      toast.error('Failed to load comparison data');
    } finally {
      setLoading(false);
    }
  }, [compareMode, fySelections, qtrSelections, monthSelections, customRanges]);

  useEffect(() => {
    loadComparison();
  }, [loadComparison]);

  const updateFY = (idx, val) => {
    const next = [...fySelections];
    next[idx] = parseInt(val);
    setFySelections(next);
  };

  const updateQtr = (idx, field, val) => {
    const next = [...qtrSelections];
    next[idx] = { ...next[idx], [field]: parseInt(val) };
    setQtrSelections(next);
  };

  const updateMonth = (idx, field, val) => {
    const next = [...monthSelections];
    next[idx] = { ...next[idx], [field]: parseInt(val) };
    setMonthSelections(next);
  };

  const updateCustomRange = (idx, field, val) => {
    const next = [...customRanges];
    next[idx] = { ...next[idx], [field]: val };
    setCustomRanges(next);
  };

  const periods = compareData?.periods || [];
  const states = compareData?.states || [];
  const national = compareData?.national_summary || [];

  return (
    <div className="space-y-4" data-testid="comparison-view">
      {/* Compare Mode Tabs + Selectors */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          {COMPARE_MODES.map(cm => (
            <Button key={cm.key} size="sm"
              variant={compareMode === cm.key ? 'default' : 'outline'}
              onClick={() => setCompareMode(cm.key)}
              className={compareMode === cm.key ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
              data-testid={`compare-mode-${cm.key}`}>
              {cm.label}
            </Button>
          ))}
        </div>

        {/* FY Selectors */}
        {compareMode === 'fy' && (
          <div className="flex flex-wrap items-center gap-2">
            {fySelections.map((fy, idx) => (
              <div key={idx} className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: COMPARE_COLORS[idx] }} />
                <select value={fy} onChange={(e) => updateFY(idx, e.target.value)}
                  className="border rounded px-2 py-1 text-sm bg-white" data-testid={`compare-fy-${idx}`}>
                  {fyOptions.map(o => (
                    <option key={o.start_year} value={o.start_year}>{o.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}

        {/* Quarterly Selectors */}
        {compareMode === 'quarterly' && (
          <div className="flex flex-wrap items-center gap-2">
            {qtrSelections.map((sel, idx) => (
              <div key={idx} className="flex items-center gap-1 border rounded-lg px-2 py-1 bg-white">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: COMPARE_COLORS[idx] }} />
                <select value={sel.q} onChange={(e) => updateQtr(idx, 'q', e.target.value)}
                  className="border-0 text-sm bg-transparent focus:ring-0 pr-1" data-testid={`compare-qtr-q-${idx}`}>
                  {FY_QUARTERS.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
                </select>
                <select value={sel.fy} onChange={(e) => updateQtr(idx, 'fy', e.target.value)}
                  className="border-0 text-sm bg-transparent focus:ring-0" data-testid={`compare-qtr-fy-${idx}`}>
                  {fyOptions.map(o => <option key={o.start_year} value={o.start_year}>{o.label}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}

        {/* Monthly Selectors */}
        {compareMode === 'monthly' && (
          <div className="flex flex-wrap items-center gap-2">
            {monthSelections.map((sel, idx) => (
              <div key={idx} className="flex items-center gap-1 border rounded-lg px-2 py-1 bg-white">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: COMPARE_COLORS[idx] }} />
                <select value={sel.month} onChange={(e) => updateMonth(idx, 'month', e.target.value)}
                  className="border-0 text-sm bg-transparent focus:ring-0 pr-1" data-testid={`compare-month-m-${idx}`}>
                  {MONTHS_LIST.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                <select value={sel.year} onChange={(e) => updateMonth(idx, 'year', e.target.value)}
                  className="border-0 text-sm bg-transparent focus:ring-0" data-testid={`compare-month-y-${idx}`}>
                  {yearList.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}

        {/* Custom Range Selectors */}
        {compareMode === 'custom' && (
          <div className="flex flex-wrap gap-3">
            {customRanges.map((range, idx) => (
              <div key={idx} className="flex items-center gap-1 border rounded-lg px-2 py-1 bg-white">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: COMPARE_COLORS[idx] }} />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-xs h-7 px-2"
                      data-testid={`compare-custom-start-${idx}`}>
                      {range.start ? fmtDateDMY(range.start) : 'Start'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={range.start}
                      onSelect={(d) => updateCustomRange(idx, 'start', d)} initialFocus />
                  </PopoverContent>
                </Popover>
                <span className="text-gray-400 text-xs">-</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-xs h-7 px-2"
                      data-testid={`compare-custom-end-${idx}`}>
                      {range.end ? fmtDateDMY(range.end) : 'End'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={range.end}
                      onSelect={(d) => updateCustomRange(idx, 'end', d)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* National Summary Comparison */}
          <Card data-testid="compare-national-summary">
            <CardHeader className="p-3 pb-1">
              <CardTitle className="text-sm text-gray-700">National Net Sales Comparison</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-1">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {national.map((n, idx) => (
                  <div key={idx} className="p-3 rounded-lg border-2" style={{ borderColor: COMPARE_COLORS[idx] + '40' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: COMPARE_COLORS[idx] }} />
                      <span className="text-xs font-semibold text-gray-600">{n.label}</span>
                    </div>
                    <p className="text-xl font-bold" style={{ color: COMPARE_COLORS[idx] }}>
                      {fmt(n.net_turnover)}
                    </p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                      <span>Orders: {n.orders}</span>
                      <span>Pcs: {n.pieces}</span>
                    </div>
                    {n.growth !== undefined && n.growth !== null && (
                      <div className="mt-1">
                        <GrowthBadge growth={n.growth} />
                        <span className="text-[10px] text-gray-400 ml-1">vs next</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* State-wise Comparison Table */}
          <Card>
            <CardHeader className="p-3 pb-1">
              <CardTitle className="text-sm text-gray-700">State-wise Comparison</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-1">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="compare-states-table">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-2 font-semibold text-gray-600 sticky left-0 bg-gray-50">#</th>
                      <th className="text-left p-2 font-semibold text-gray-600 sticky left-8 bg-gray-50">State</th>
                      <th className="text-right p-2 font-semibold text-gray-600">Customers</th>
                      {periods.map((p, idx) => (
                        <th key={idx} className="text-right p-2 font-semibold" style={{ color: COMPARE_COLORS[idx] }}>
                          {p.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {states.map((s, i) => {
                      const isExpanded = expandedState === s.state;
                      return (
                        <React.Fragment key={i}>
                          <tr className={`border-b cursor-pointer transition-colors ${isExpanded ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}
                            onClick={() => setExpandedState(isExpanded ? null : s.state)}>
                            <td className="p-2 text-gray-400 sticky left-0 bg-inherit">{i + 1}</td>
                            <td className="p-2 font-medium text-gray-900 sticky left-8 bg-inherit">
                              <span className="flex items-center gap-1">
                                {s.state}
                                <svg className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                              </span>
                            </td>
                            <td className="p-2 text-right text-purple-700 font-semibold">{s.customer_count}</td>
                            {periods.map((p, idx) => {
                              const pd = s.period_data?.[p.label] || {};
                              return (
                                <td key={idx} className="p-2 text-right font-bold" style={{ color: COMPARE_COLORS[idx] }}>
                                  {fmt(pd.net_turnover || 0)}
                                </td>
                              );
                            })}
                          </tr>
                          {isExpanded && (
                            <tr className="bg-indigo-50/50">
                              <td colSpan={3 + periods.length} className="p-0">
                                <div className="px-6 py-2">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                    {periods.map((p, idx) => {
                                      const pd = s.period_data?.[p.label] || {};
                                      return (
                                        <div key={idx} className="p-2 rounded border bg-white">
                                          <p className="font-semibold mb-1" style={{ color: COMPARE_COLORS[idx] }}>{p.label}</p>
                                          <p>Net Sales: <strong>{fmt(pd.net_turnover || 0)}</strong></p>
                                          <p>Orders: {pd.orders || 0}</p>
                                          <p>Pieces: {pd.pieces || 0}</p>
                                          <p>Customers: {pd.customer_count || 0}</p>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {/* Totals row */}
                    <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                      <td className="p-2"></td>
                      <td className="p-2 text-gray-900">Total</td>
                      <td className="p-2 text-right text-purple-700">
                        {states.reduce((sum, s) => sum + (s.customer_count || 0), 0)}
                      </td>
                      {national.map((n, idx) => (
                        <td key={idx} className="p-2 text-right" style={{ color: COMPARE_COLORS[idx] }}>
                          {fmt(n.net_turnover)}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};


// =====================
// MAIN DASHBOARD VIEW
// =====================
const MainDashboardView = ({ dashData, filterType, setFilterType, selectedYear, setSelectedYear,
  selectedQuarter, setSelectedQuarter, customStart, setCustomStart, customEnd, setCustomEnd,
  loading, selectedState, setSelectedState, showAgents, setShowAgents,
  showDistributors, setShowDistributors }) => {

  const [hoveredState, setHoveredState] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [mapCenter, setMapCenter] = useState([82, 22]);
  const [mapZoom, setMapZoom] = useState(1);
  const [zoomedState, setZoomedState] = useState(null);

  const stateDataMap = useMemo(() => {
    if (!dashData?.states) return {};
    const map = {};
    dashData.states.forEach(s => { map[s.state] = s; });
    return map;
  }, [dashData]);

  const maxTurnover = useMemo(() => {
    if (!dashData?.states) return 0;
    return Math.max(...dashData.states.map(s => Math.abs(s.net_turnover || 0)), 1);
  }, [dashData]);

  const handleStateClick = (stateName) => {
    const normalized = STATE_NAME_MAP[stateName] || stateName;
    const sd = stateDataMap[normalized];
    const stateInfo = sd || {
      state: normalized, net_turnover: 0, current_net: 0, prev_net: 0,
      growth: 0, orders: 0, pieces: 0, customer_count: 0, cities: []
    };
    setSelectedState(stateInfo);
    const centers = dashData?.state_centers || {};
    const center = centers[normalized];
    if (center) {
      setMapCenter([center[1], center[0]]);
      setMapZoom(4);
      setZoomedState(normalized);
    }
  };

  const handleResetZoom = () => {
    setMapCenter([82, 22]);
    setMapZoom(1);
    setZoomedState(null);
  };

  const handleMouseMove = (e, stateName) => {
    const normalized = STATE_NAME_MAP[stateName] || stateName;
    setHoveredState(stateDataMap[normalized] || { state: normalized, net_turnover: 0 });
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  const summary = dashData?.summary || {};
  const fyOptions = useMemo(() => generateFYOptions(dashData?.available_fys), [dashData]);
  const curPeriod = dashData?.current_period || {};
  const prevPeriod = dashData?.previous_period || {};

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="geo-dashboard-loading">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2" data-testid="geo-filters">
        {['yearly', 'quarterly', 'custom'].map(ft => (
          <Button key={ft} size="sm"
            variant={filterType === ft ? 'default' : 'outline'}
            onClick={() => setFilterType(ft)}
            className={filterType === ft ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
            data-testid={`filter-${ft}`}>
            {ft === 'yearly' ? 'Yearly' : ft === 'quarterly' ? 'Quarterly' : 'Custom'}
          </Button>
        ))}

        <span className="text-gray-300">|</span>

        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          className="border rounded px-2 py-1 text-sm bg-white"
          data-testid="year-select"
        >
          {fyOptions.map(o => (
            <option key={o.start_year} value={o.start_year}>{o.label}</option>
          ))}
        </select>

        {filterType === 'quarterly' && (
          <select
            value={selectedQuarter}
            onChange={(e) => setSelectedQuarter(e.target.value)}
            className="border rounded px-2 py-1 text-sm bg-white"
            data-testid="quarter-select"
          >
            {FY_QUARTERS.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
          </select>
        )}

        {filterType === 'custom' && (
          <div className="flex items-center gap-1">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="text-xs" data-testid="custom-start-date">
                  {customStart ? fmtDateDMY(customStart) : 'Start Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={customStart} onSelect={setCustomStart} initialFocus />
              </PopoverContent>
            </Popover>
            <span className="text-gray-400 text-xs">to</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="text-xs" data-testid="custom-end-date">
                  {customEnd ? fmtDateDMY(customEnd) : 'End Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={customEnd} onSelect={setCustomEnd} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="geo-summary-cards">
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="p-3">
            <p className="text-xs text-gray-500">Net Turnover</p>
            <p className="text-lg font-bold text-indigo-700" data-testid="national-turnover">
              {fmt(summary.national_turnover || 0)}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-3">
            <p className="text-xs text-gray-500">{curPeriod.label || 'Current'}</p>
            <p className="text-lg font-bold text-emerald-700">{fmt(summary.national_current || 0)}</p>
            <GrowthBadge growth={summary.national_growth} />
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-3">
            <p className="text-xs text-gray-500">{prevPeriod.label || 'Previous'}</p>
            <p className="text-lg font-bold text-amber-700">{fmt(summary.national_previous || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-3">
            <p className="text-xs text-gray-500">Active States</p>
            <p className="text-lg font-bold text-blue-700">{summary.total_states || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-3">
            <p className="text-xs text-gray-500">Total Customers</p>
            <p className="text-lg font-bold text-purple-700" data-testid="total-customers">
              {summary.total_customers || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-rose-500">
          <CardContent className="p-3">
            <p className="text-xs text-gray-500">Growth</p>
            <p className="text-lg font-bold"><GrowthBadge growth={summary.national_growth} /></p>
          </CardContent>
        </Card>
      </div>

      {/* Period Comparison */}
      {(summary.national_previous > 0 || summary.national_current > 0) && (
        <Card className="bg-gradient-to-r from-slate-50 to-indigo-50 border-slate-200" data-testid="comparison-panel">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Period Comparison</p>
            <div className="flex items-center gap-6">
              <div>
                <p className="text-xs text-gray-500">{curPeriod.label}</p>
                <p className="text-xl font-bold text-emerald-700">{fmt(summary.national_current || 0)}</p>
              </div>
              <div className="text-2xl text-gray-300">vs</div>
              <div>
                <p className="text-xs text-gray-500">{prevPeriod.label}</p>
                <p className="text-xl font-bold text-amber-700">{fmt(summary.national_previous || 0)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Change</p>
                <p className="text-xl font-bold"><GrowthBadge growth={summary.national_growth} /></p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Growing / Declining */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="p-3 pb-1">
            <CardTitle className="text-sm text-green-700 flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              Top Growing States
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-1">
            {(summary.top_growing || []).length === 0 ? <p className="text-sm text-gray-400">No growth data yet</p> : (
              <div className="space-y-1.5">
                {(summary.top_growing || []).map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm cursor-pointer hover:bg-green-50 px-2 py-1 rounded"
                    onClick={() => handleStateClick(s.state)}>
                    <span className="font-medium text-gray-800">{s.state}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">{fmt(s.current)}</span>
                      <GrowthBadge growth={s.growth} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-3 pb-1">
            <CardTitle className="text-sm text-red-700 flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
              </svg>
              Declining States
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-1">
            {(summary.top_declining || []).length === 0 ? <p className="text-sm text-gray-400">No decline data yet</p> : (
              <div className="space-y-1.5">
                {(summary.top_declining || []).map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm cursor-pointer hover:bg-red-50 px-2 py-1 rounded"
                    onClick={() => handleStateClick(s.state)}>
                    <span className="font-medium text-gray-800">{s.state}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">{fmt(s.current)}</span>
                      <GrowthBadge growth={s.growth} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Map */}
      <Card>
        <CardHeader className="p-3 pb-0">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <CardTitle className="text-base">India Net Sales Map</CardTitle>
              {zoomedState && (
                <>
                  <Button size="sm" variant="outline" onClick={handleResetZoom}
                    className="text-xs" data-testid="reset-zoom-btn">
                    <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                    </svg>
                    Reset Zoom
                  </Button>
                  <Badge className="bg-indigo-100 text-indigo-700 text-xs">Zoomed: {zoomedState}</Badge>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant={showAgents ? 'default' : 'outline'}
                onClick={() => { setShowAgents(!showAgents); setShowDistributors(false); }}
                className={showAgents ? 'bg-violet-600 hover:bg-violet-700' : ''}
                data-testid="toggle-agents-overlay">
                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Agent
              </Button>
              <Button size="sm" variant={showDistributors ? 'default' : 'outline'}
                onClick={() => { setShowDistributors(!showDistributors); setShowAgents(false); }}
                className={showDistributors ? 'bg-teal-600 hover:bg-teal-700' : ''}
                data-testid="toggle-distributors-overlay">
                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Distributor
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: '#059669' }} /> Growing (&gt;20%)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: '#34d399' }} /> Growing (0-20%)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: '#fbbf24' }} /> Flat</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: '#f97316' }} /> Declining (0-20%)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: '#ef4444' }} /> Declining (&gt;20%)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: '#e5e7eb' }} /> No data</span>
          </div>
        </CardHeader>
        <CardContent className="p-2 relative">
          {hoveredState && (
            <div className="fixed z-50 pointer-events-none bg-gray-900 text-white px-3 py-2 rounded-lg shadow-xl text-xs"
              style={{ left: tooltipPos.x + 12, top: tooltipPos.y - 10 }}>
              <p className="font-bold text-sm">{hoveredState.state}</p>
              <p>Net Sales: <span className="text-emerald-300 font-bold">{fmt(hoveredState.net_turnover || 0)}</span></p>
              <p>Customers: <span className="text-blue-300 font-bold">{hoveredState.customer_count || 0}</span></p>
              {hoveredState.growth !== undefined && (
                <p>Growth: <span className={hoveredState.growth >= 0 ? 'text-green-300' : 'text-red-300'}>
                  {hoveredState.growth > 0 ? '+' : ''}{hoveredState.growth}%
                </span></p>
              )}
              <p className="text-gray-400 mt-1 text-[10px]">Click to zoom & view cities</p>
            </div>
          )}
          <div className="w-full" style={{ height: '560px' }} data-testid="india-map">
            <ComposableMap projection="geoMercator" projectionConfig={{ scale: 1000, center: [82, 22] }}
              style={{ width: '100%', height: '100%' }}>
              <ZoomableGroup center={mapCenter} zoom={mapZoom} minZoom={0.8} maxZoom={8}
                onMoveEnd={({ coordinates, zoom }) => { setMapCenter(coordinates); setMapZoom(zoom); }}>
                <Geographies geography={GEO_URL}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const geoName = geo.properties.ST_NM || geo.properties.NAME_1 || '';
                      const normalized = STATE_NAME_MAP[geoName] || geoName;
                      const sd = stateDataMap[normalized];
                      const fill = sd ? getColor(sd.net_turnover, maxTurnover, sd.growth) : '#e5e7eb';
                      const isSelected = selectedState?.state === normalized;
                      return (
                        <Geography key={geo.rsmKey} geography={geo}
                          fill={isSelected ? '#6366f1' : fill} stroke="#fff"
                          strokeWidth={isSelected ? 1.5 : 0.5}
                          onClick={() => handleStateClick(geoName)}
                          onMouseMove={(e) => handleMouseMove(e, geoName)}
                          onMouseLeave={() => setHoveredState(null)}
                          style={{
                            default: { outline: 'none', cursor: 'pointer' },
                            hover: { fill: isSelected ? '#6366f1' : '#818cf8', outline: 'none', cursor: 'pointer', strokeWidth: 1 },
                            pressed: { outline: 'none' },
                          }} />
                      );
                    })
                  }
                </Geographies>
                {zoomedState && selectedState?.cities?.map((city, i) => {
                  const coords = CITY_COORDS[city.city];
                  if (!coords) return null;
                  return (
                    <Marker key={i} coordinates={[coords[1], coords[0]]}>
                      <circle r={3} fill="#4f46e5" stroke="#fff" strokeWidth={1} />
                      <text textAnchor="middle" y={-8}
                        style={{ fontFamily: 'Nunito, sans-serif', fontSize: '4px', fontWeight: 700, fill: '#1e1b4b' }}>
                        {city.city}
                      </text>
                      <text textAnchor="middle" y={-4}
                        style={{ fontFamily: 'Nunito, sans-serif', fontSize: '3px', fill: '#059669' }}>
                        {fmt(city.net_turnover)}
                      </text>
                    </Marker>
                  );
                })}
              </ZoomableGroup>
            </ComposableMap>
          </div>
        </CardContent>
      </Card>

      {/* State Detail Panel */}
      {selectedState && (
        <Card className="border-2 border-indigo-300" data-testid="state-detail-panel">
          <CardHeader className="p-4 pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  {selectedState.state}
                  <Badge className="bg-purple-100 text-purple-700 text-xs">
                    {selectedState.customer_count || 0} Customers
                  </Badge>
                </CardTitle>
                <p className="text-xs text-gray-500 mt-1">
                  Net Turnover: <strong className="text-indigo-700">{fmt(selectedState.net_turnover)}</strong>
                  {' | '}Orders: {selectedState.orders}{' | '}Pcs: {selectedState.pieces}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-xs text-gray-500">Current: <strong className="text-emerald-700">{fmt(selectedState.current_net)}</strong></p>
                  <p className="text-xs text-gray-500">Previous: <strong className="text-amber-700">{fmt(selectedState.prev_net)}</strong></p>
                </div>
                <GrowthBadge growth={selectedState.growth} />
                <Button size="sm" variant="ghost" onClick={() => { setSelectedState(null); handleResetZoom(); }}
                  data-testid="close-state-panel">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {(showAgents || showDistributors) && (
              <div className="mb-4">
                <h4 className="text-sm font-bold text-gray-700 mb-2">
                  {showAgents ? 'Agents' : 'Distributors'} in {selectedState.state}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {(showAgents
                    ? (dashData?.agent_territories?.[selectedState.state] || [])
                    : (dashData?.distributor_territories?.[selectedState.state] || [])
                  ).map((t, i) => (
                    <div key={i} className={`p-3 rounded-lg border ${showAgents ? 'bg-violet-50 border-violet-200' : 'bg-teal-50 border-teal-200'}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-sm text-gray-900">{t.name}</span>
                        <Badge className="text-xs bg-gray-100 text-gray-600">{t.customer_count} customers</Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
                        <span>Net Sales: <strong className="text-emerald-700">{fmt(t.turnover)}</strong></span>
                        <span>Orders: <strong>{t.orders}</strong></span>
                      </div>
                    </div>
                  ))}
                  {(showAgents
                    ? (dashData?.agent_territories?.[selectedState.state] || [])
                    : (dashData?.distributor_territories?.[selectedState.state] || [])
                  ).length === 0 && (
                    <p className="text-sm text-gray-400 col-span-3">
                      No {showAgents ? 'agents' : 'distributors'} assigned
                    </p>
                  )}
                </div>
              </div>
            )}
            <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
              <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              City-wise Breakdown
            </h4>
            {(selectedState.cities || []).length === 0 ? (
              <p className="text-sm text-gray-400">No city-level data available</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="city-breakdown-table">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-2 font-semibold text-gray-600">City</th>
                      <th className="text-right p-2 font-semibold text-gray-600">Net Turnover</th>
                      <th className="text-right p-2 font-semibold text-gray-600">Current</th>
                      <th className="text-right p-2 font-semibold text-gray-600">Previous</th>
                      <th className="text-right p-2 font-semibold text-gray-600">Growth</th>
                      <th className="text-right p-2 font-semibold text-gray-600">Customers</th>
                      <th className="text-right p-2 font-semibold text-gray-600">Orders</th>
                      <th className="text-right p-2 font-semibold text-gray-600">Pcs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedState.cities || []).map((city, i) => (
                      <tr key={i} className="border-b hover:bg-indigo-50 transition-colors">
                        <td className="p-2 font-medium text-gray-900 flex items-center gap-1">
                          <svg className="w-3 h-3 text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                          {city.city}
                        </td>
                        <td className="p-2 text-right font-bold text-indigo-700">{fmt(city.net_turnover)}</td>
                        <td className="p-2 text-right text-emerald-700">{fmt(city.current_net)}</td>
                        <td className="p-2 text-right text-amber-700">{fmt(city.prev_net)}</td>
                        <td className="p-2 text-right"><GrowthBadge growth={city.growth} /></td>
                        <td className="p-2 text-right text-purple-700 font-semibold">{city.customer_count || 0}</td>
                        <td className="p-2 text-right text-gray-600">{city.orders}</td>
                        <td className="p-2 text-right text-gray-600">{city.pieces}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* All States Table */}
      <Card>
        <CardHeader className="p-3 pb-1"><CardTitle className="text-base">All States Overview</CardTitle></CardHeader>
        <CardContent className="p-3 pt-1">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="all-states-table">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-2 font-semibold text-gray-600">#</th>
                  <th className="text-left p-2 font-semibold text-gray-600">State</th>
                  <th className="text-right p-2 font-semibold text-gray-600">Net Turnover</th>
                  <th className="text-right p-2 font-semibold text-gray-600">Current</th>
                  <th className="text-right p-2 font-semibold text-gray-600">Previous</th>
                  <th className="text-right p-2 font-semibold text-gray-600">Growth</th>
                  <th className="text-right p-2 font-semibold text-gray-600">Customers</th>
                  <th className="text-right p-2 font-semibold text-gray-600">Orders</th>
                  <th className="text-right p-2 font-semibold text-gray-600">Pcs</th>
                  {showAgents && <th className="text-left p-2 font-semibold text-violet-600">Agents</th>}
                  {showDistributors && <th className="text-left p-2 font-semibold text-teal-600">Distributors</th>}
                </tr>
              </thead>
              <tbody>
                {(dashData?.states || []).map((s, i) => (
                  <tr key={i}
                    className={`border-b hover:bg-indigo-50 cursor-pointer transition-colors ${selectedState?.state === s.state ? 'bg-indigo-100' : ''}`}
                    onClick={() => handleStateClick(s.state)}>
                    <td className="p-2 text-gray-400">{i + 1}</td>
                    <td className="p-2 font-medium text-gray-900">{s.state}</td>
                    <td className="p-2 text-right font-bold text-indigo-700">{fmt(s.net_turnover)}</td>
                    <td className="p-2 text-right text-emerald-700">{fmt(s.current_net)}</td>
                    <td className="p-2 text-right text-amber-700">{fmt(s.prev_net)}</td>
                    <td className="p-2 text-right"><GrowthBadge growth={s.growth} /></td>
                    <td className="p-2 text-right text-purple-700 font-semibold">{s.customer_count || 0}</td>
                    <td className="p-2 text-right text-gray-600">{s.orders}</td>
                    <td className="p-2 text-right text-gray-600">{s.pieces}</td>
                    {showAgents && (
                      <td className="p-2">
                        {(dashData?.agent_territories?.[s.state] || []).map(a => (
                          <Badge key={a.id} className="text-xs bg-violet-100 text-violet-700 mr-1 mb-0.5">{a.name}</Badge>
                        ))}
                      </td>
                    )}
                    {showDistributors && (
                      <td className="p-2">
                        {(dashData?.distributor_territories?.[s.state] || []).map(d => (
                          <Badge key={d.id} className="text-xs bg-teal-100 text-teal-700 mr-1 mb-0.5">{d.name}</Badge>
                        ))}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


// =====================
// MAIN WRAPPER
// =====================
const GeoDashboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState('dashboard'); // dashboard or compare
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('yearly');
  const [selectedYear, setSelectedYear] = useState(String(currentFYStart()));
  const [selectedQuarter, setSelectedQuarter] = useState(currentFYQuarter());
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [showAgents, setShowAgents] = useState(false);
  const [showDistributors, setShowDistributors] = useState(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      let params = `filter_type=${filterType}&year=${selectedYear}`;
      if (filterType === 'quarterly') {
        params += `&quarter=${selectedQuarter}`;
      } else if (filterType === 'custom' && customStart && customEnd) {
        const sd = customStart.toISOString().slice(0, 10);
        const ed = customEnd.toISOString().slice(0, 10);
        params += `&start_date=${sd}&end_date=${ed}`;
      }
      const res = await api.get(`/geo-dashboard?${params}`);
      setDashData(res.data);
    } catch {
      toast.error('Failed to load geo dashboard');
    } finally {
      setLoading(false);
    }
  }, [filterType, selectedYear, selectedQuarter, customStart, customEnd]);

  useEffect(() => {
    if (activeTab === 'dashboard') loadDashboard();
  }, [loadDashboard, activeTab]);

  const curPeriod = dashData?.current_period || {};

  return (
    <div className="space-y-5 p-4 md:p-6" data-testid="geo-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>
            India Sales Map
          </h1>
          <p className="text-sm text-gray-500">
            Net Sales (SE - SR) by State & City
            {activeTab === 'dashboard' && curPeriod.label && ` | ${curPeriod.label}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant={activeTab === 'dashboard' ? 'default' : 'outline'}
            onClick={() => setActiveTab('dashboard')}
            className={activeTab === 'dashboard' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
            data-testid="tab-dashboard">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            Dashboard
          </Button>
          <Button size="sm" variant={activeTab === 'compare' ? 'default' : 'outline'}
            onClick={() => setActiveTab('compare')}
            className={activeTab === 'compare' ? 'bg-orange-600 hover:bg-orange-700' : ''}
            data-testid="tab-compare">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Compare
          </Button>
        </div>
      </div>

      {activeTab === 'dashboard' ? (
        <MainDashboardView
          dashData={dashData} loading={loading}
          filterType={filterType} setFilterType={setFilterType}
          selectedYear={selectedYear} setSelectedYear={setSelectedYear}
          selectedQuarter={selectedQuarter} setSelectedQuarter={setSelectedQuarter}
          customStart={customStart} setCustomStart={setCustomStart}
          customEnd={customEnd} setCustomEnd={setCustomEnd}
          selectedState={selectedState} setSelectedState={setSelectedState}
          showAgents={showAgents} setShowAgents={setShowAgents}
          showDistributors={showDistributors} setShowDistributors={setShowDistributors}
        />
      ) : (
        <ComparisonView user={user} />
      )}
    </div>
  );
};

export default GeoDashboard;
